import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import {
  Settings as SettingsIcon, Trash2, AlertTriangle, Shield, Loader2,
  Lock, Download, Eye, EyeOff, CheckCircle
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api/client';

const Settings = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Change password state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  // Export data state
  const [exporting, setExporting] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') return;
    setDeleting(true);
    try {
      await api.delete('/auth/delete-account');
      toast({ title: 'Account Deleted', description: 'Your account and all personal data have been permanently removed.' });
      logout();
      navigate('/');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to delete account.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'New passwords do not match.', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'Password must be at least 8 characters.', variant: 'destructive' });
      return;
    }
    setChangingPassword(true);
    try {
      const response = await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      // Update token if a new one was issued
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
      }
      setPasswordChanged(true);
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: 'Password Changed', description: 'Your password has been updated successfully.' });
      setTimeout(() => setPasswordChanged(false), 5000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to change password.';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await api.get('/auth/export-data');
      const data = response.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wayo-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Export Complete', description: 'Your data has been downloaded.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export data.', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="settings-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-emerald-100 p-2.5 rounded-xl">
            <SettingsIcon className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="text-gray-500 text-sm">Manage your account, security, and privacy</p>
          </div>
        </div>

        {/* Account Info */}
        <Card className="mb-6" data-testid="account-info-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-500" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm">Email</span>
              <span className="text-gray-900 font-medium text-sm">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-500 text-sm">Name</span>
              <span className="text-gray-900 font-medium text-sm">{user?.first_name} {user?.last_name}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-500 text-sm">Role</span>
              <Badge variant="outline" className="capitalize">{user?.role}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="mb-6" data-testid="change-password-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="h-5 w-5 text-blue-500" />
              Password
            </CardTitle>
            <CardDescription>
              {passwordChanged
                ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="h-4 w-4" /> Password updated successfully</span>
                : 'Update your password. You\'ll need your current password to make changes.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showPasswordForm ? (
              <Button
                variant="outline"
                onClick={() => setShowPasswordForm(true)}
                data-testid="change-password-button"
              >
                <Lock className="h-4 w-4 mr-1.5" /> Change Password
              </Button>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      data-testid="current-password-input"
                    />
                    <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {newPassword && newPassword.length < 8 && (
                    <p className="text-xs text-red-500">Must be at least 8 characters</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500">Passwords do not match</p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={changingPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="submit-password-button"
                  >
                    {changingPassword ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Updating...</> : 'Update Password'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowPasswordForm(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Export Data */}
        <Card className="mb-6" data-testid="export-data-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Download className="h-5 w-5 text-purple-500" />
              Your Data
            </CardTitle>
            <CardDescription>Download a copy of all your personal data stored on Wayo.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div>
                <p className="font-medium text-gray-900 text-sm">Export My Data</p>
                <p className="text-gray-500 text-xs mt-0.5">Download your profile, applications, and activity as JSON</p>
              </div>
              <Button
                variant="outline"
                onClick={handleExportData}
                disabled={exporting}
                className="border-purple-300 text-purple-600 hover:bg-purple-50"
                data-testid="export-data-button"
              >
                {exporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting...</> : <><Download className="h-4 w-4 mr-1.5" /> Download</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200" data-testid="danger-zone-card">
          <CardHeader>
            <CardTitle className="text-lg text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>Irreversible actions that affect your account and data.</CardDescription>
          </CardHeader>
          <CardContent>
            {!showDeleteConfirm ? (
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-100">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Delete Account</p>
                  <p className="text-gray-500 text-xs mt-0.5">Permanently remove your account and all personal data</p>
                </div>
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setShowDeleteConfirm(true)}
                  data-testid="delete-account-button"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              </div>
            ) : (
              <div className="p-5 bg-red-50 rounded-xl border border-red-200 space-y-4" data-testid="delete-confirm-section">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-800">This action is permanent</p>
                    <p className="text-red-700 text-sm mt-1">
                      This will permanently delete your account, all saved applications,
                      essay drafts, credit balance, and personal information. This cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-red-700 text-sm">Type <strong>DELETE</strong> to confirm</Label>
                  <Input
                    value={deleteInput}
                    onChange={(e) => setDeleteInput(e.target.value)}
                    placeholder="Type DELETE"
                    className="border-red-200 focus:border-red-400 focus:ring-red-400"
                    data-testid="delete-confirm-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleteInput !== 'DELETE' || deleting}
                    className="flex-1"
                    data-testid="delete-confirm-button"
                  >
                    {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="h-4 w-4 mr-1.5" /> Permanently Delete Account</>}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteInput(''); }} data-testid="delete-cancel-button">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
